<script setup lang="ts">
import { useCalendarStore } from "@/store/useCalendarStore";
import { useGlobalErrorStore } from "@/store/useGlobalError";
import { moment } from "@/utils/useTimeZone";
import { useBooking } from "./useBooking";

interface Props {
  booking: any
  isBookingViewOnly: boolean
  currentStep: number
  bookingSteps: any[]
  isEditBooking: any
  role: any
  haveEditAccess: boolean
  haveCreateAccess: boolean
  bookingStatus: string
  isYetToPayDues: boolean
  isDirty: boolean
  canEditBooking: boolean
  canPayInStore: boolean
  isPayInStoreSelected: boolean
  recurringDetails: any
  activityList: any[]
  userId: string
}

interface Emits {
  (e: 'editBooking'): void
  (e: 'prevStep'): void
  (e: 'refetchBookingData', bookingId: string): void
  (e: 'closeDialogForPayLater'): void
  (e: 'closeDialog'): void
  (e: 'handleSubmit'): void
  (e: 'handlePaymentDues'): void
  (e: 'goToNextStep'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const booking = useBooking();
const calendar = useCalendarStore();
const globalErrorStore = useGlobalErrorStore();

const { handleTicketPrint } = booking;
const { deleteBooking } = calendar;
const { handleUpdateErrorMessage } = globalErrorStore;

const isQrCodeVisible = ref(false);
const printTicketIsVisible = ref(false);
const isDeleteDialogVisible = ref(false);

const activity= computed(() => props.activityList?.find(
  (activity: any) => activity.id === props?.booking?.activity,
));

const isActionButtonDisabled = computed(() => {
  if (props?.booking?.id) {
    if (props.haveEditAccess) {
      // admin so allow them to edit
      return false;
    } else if (
      // a guest booking has no member to match on, so the booking link itself grants edit
      props?.booking?.source === "Guest Booking" ||
      props?.booking?.users?.includes(props?.userId)
    ) {
      if (props.bookingStatus === "futureBooking") {
        // future booking so allow them to edit
        return false;
      } else {
        return true;
      }
    } else return true;
  } else {
    return false;
  }
});

const isSaveDisabled = computed(
  () => isActionButtonDisabled.value || !props.isDirty
);

/**
 * Handles the deletion of a booking.
 * Allows admins to delete any booking and users to delete only eligible bookings.
 *
 * @param {boolean} isAll - Indicates whether to delete all recurring bookings.
 * @returns {boolean} - Returns false if the booking is within the non-cancellation window.
 */
const checkDelete = (isAll: boolean) => {
  const now = moment();

  if (moment(props.booking?.endTime).utc().isBefore(now.utc())) {
    handleUpdateErrorMessage(true, "Unable to cancel the booking as it is in past time.");
    isDeleteDialogVisible.value = false;
    return false;
  }

  const isAdmin = props.role === "Admin" || props.haveEditAccess || props.haveCreateAccess;

  if (!isAdmin) {
    const startTime = moment(props.booking?.startTime);
    const endTime = startTime.clone().add(props.booking.duration, "minutes");
    const isCurrentBooking = now.isBetween(startTime, endTime);
    const minutesSinceCreated = moment(props.booking.created).diff(now, "minutes");
    const hoursUntilStart = startTime.diff(now, "hours");
    const isNewBooking = minutesSinceCreated <= 0 && minutesSinceCreated >= -15 && !isCurrentBooking;
    const isOutsideCancellationWindow = startTime.isAfter(now) && hoursUntilStart > activity.value.cancellationHours;

    if (!isNewBooking && !isOutsideCancellationWindow) {
      handleUpdateErrorMessage(true, "Unable to cancel the booking as it is within the non-cancellation window.");
      isDeleteDialogVisible.value = false;
      return false;
    }
  }

  deleteBooking({
    bookingId: props.booking.id,
    recurringId: props.recurringDetails?.recurringId || "",
    isAll,
  });
};
</script>
<template>
  <VCardText class="button_container">
  <div class="d-flex button_group gap-3">
    <div class="d-flex flex-wrap ps-0 gap-3">
      <div class="d-flex ps-0" v-if="props.canEditBooking">
      <VBtn
        v-if="props.isBookingViewOnly"
        color="secondary"
        @click.prevent="emit('editBooking')">
        Edit Booking
      </VBtn>
      <VBtn
        v-else
        :disabled="props.currentStep === 0"
        color="secondary"
        @click.prevent="emit('prevStep')">
        <VIcon icon="mdi-arrow-left" start class="flip-in-rtl" />
        Previous
      </VBtn>
      </div>

      <VBtn
        v-if="props.isEditBooking"
        color="success"
        @click.prevent="printTicketIsVisible = true"
      >
        <VIcon icon="mdi-receipt-text-send" start class="flip-in-rtl" />
        Print
      </VBtn>
      <!-- QR -->
      <IconBtn
        v-if="props?.booking?.id && props.booking.state === 'Confirmed'"
        color="grey"
        icon="mdi-qrcode"
        class="rounded-sm border-md"
        border="secondary lg"
        @click.stop="isQrCodeVisible = true"
      />
    </div>
    <div class="d-flex flex-wrap justify-end gap-3">
      <div
        v-if="
          props.isEditBooking &&
          props.bookingStatus !== 'completed' &&
          !props.isBookingViewOnly
        "
      >
        <VBtn color="error" @click="isDeleteDialogVisible = true">
          Delete
        </VBtn>
      </div>
      <VBtn
        v-if="props.canPayInStore && props.isPayInStoreSelected && props.currentStep == props.bookingSteps.length - 1 && props?.booking?.state != 'Confirmed'"
        :color="props.currentStep === props.bookingSteps.length - 1 ? 'success' : 'secondary'"
        @click="emit('closeDialogForPayLater')"
      >
        {{
          props.currentStep === props.bookingSteps.length - 1
            ? "Confirm - Pay Later"
            : "Close"
        }}
      </VBtn>
      <VBtn
        v-if="props.currentStep < props.bookingSteps.length - 1 || (props?.booking?.state === 'Confirmed')"
        color="secondary"
        class="d-none d-md-inline"
        @click="emit('closeDialog')"
      >Close</VBtn>
      <!-- Edit booking -->
      <VBtn
        v-if="
          props.isEditBooking &&
          props.isYetToPayDues &&
          !props.isDirty &&
          props.bookingSteps.length - 1 !== props.currentStep &&
          !props.isBookingViewOnly
        "
        color="success"
        append-icon="mdi-arrow-right"
        @click="emit('goToNextStep')"
      >
        {{ props.currentStep === props.bookingSteps.length - 2 ? "Payment" : "Next" }}
      </VBtn>
      <VBtn
        v-if="
          props.isEditBooking &&
          (props.isDirty || !props.isYetToPayDues) &&
          props.bookingSteps.length - 1 !== props.currentStep &&
          !props.isBookingViewOnly
        "
        :disabled="isSaveDisabled"
        color="success"
        @click="emit('handleSubmit')"
      >
        {{ props.isEditBooking ? "Save" : "Payment" }}
      </VBtn>
      <VBtn
        v-if="props.isEditBooking && props.isYetToPayDues && props.isBookingViewOnly"
        color="success"
        @click="emit('handlePaymentDues')"
      >
        Pay
      </VBtn>

      <!-- create booking -->
      <div v-if="!props.isEditBooking" class="align-content-center">
        <VBtn
          v-if="props.bookingSteps.length - 2 > props.currentStep"
          class="ml-2"
          @click="emit('goToNextStep')"
        >
          Next
          <VIcon icon="mdi-arrow-right" end class="flip-in-rtl" />
        </VBtn>

        <VBtn
          v-else
          :disabled="isActionButtonDisabled"
          color="success"
          class="ml-2"
          append-icon="mdi-arrow-right"
          @click="emit('handleSubmit')"
        >
          {{ props.isEditBooking ? "Save" : "Payment" }}
        </VBtn>
      </div>
    </div>
  </div>
</VCardText>

  <TicketPrint
    :is-visible="printTicketIsVisible"
    :emailed="props?.booking?.emailed"
    :printed="props?.booking?.printed"
    :have-edit-access="props.haveEditAccess"
    :is-admin="props.role === 'Admin'"
    @print="
      async ($event: any, printer) => {
        await handleTicketPrint($event, props?.booking?.id, printer);
        emit('refetchBookingData', props?.booking?.id);
        printTicketIsVisible = false;
      }
    "
    @closeDialog="printTicketIsVisible = false"
  />

  <QrCode v-if="isQrCodeVisible"
    :is-visible="isQrCodeVisible"
    :qr-value="props.booking.entryCode"
    :booking="props?.booking"
    @closeDialog="isQrCodeVisible = false"
  />

  <!-- Delete Booking confirm dialog-->
  <BookingDeleteConfirmDialog
    :is-open="isDeleteDialogVisible"
    :is-admin="props.haveCreateAccess"
    :activity="activity"
    :booking="props?.booking"
    :is-recurring="props?.recurringDetails?.bookings?.length ? true : false"
    @closeDialog="isDeleteDialogVisible = $event"
    @cancelBooking="checkDelete"
  />

</template>
<style scoped lang="scss">
.button_group {
  display: flex;
  justify-content: space-between;
}

.docket-link {
  color: white;

  &:hover {
    color: rgb(var(--v-theme-primary));
  }
}
</style>
